import React, { useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, MenuItem,
  Alert, CircularProgress
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

const ROLES = [
  { value: 'worker', label: 'Worker' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' }
];

const TeamMemberForm = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone_number: '',
    role: 'worker',
    calendar_id: ''
  });

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await api.post('/admin/employees', {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone_number: form.phone_number.trim() || null,
        role: form.role,
        calendar_id: form.calendar_id.trim() || null
      });
      setSuccess(true);
      setTimeout(() => navigate('/team'), 1200);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to add team member';
      setError(detail);
    }
    setSaving(false);
  };

  return (
    <Box sx={{ pb: 10, maxWidth: 600, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/team')}
        sx={{ mb: 2, fontWeight: 600 }}
      >
        Back to Team
      </Button>

      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
          Add Team Member
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Team member added! Redirecting...</Alert>}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Full Name"
            value={form.name}
            onChange={handleChange('name')}
            fullWidth
            required
            sx={{ mb: 2.5 }}
          />

          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={handleChange('email')}
            fullWidth
            sx={{ mb: 2.5 }}
          />

          <TextField
            label="Phone Number"
            value={form.phone_number}
            onChange={handleChange('phone_number')}
            fullWidth
            sx={{ mb: 2.5 }}
          />

          <TextField
            label="Role"
            select
            value={form.role}
            onChange={handleChange('role')}
            fullWidth
            sx={{ mb: 2.5 }}
          >
            {ROLES.map(r => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Google Calendar ID (optional)"
            value={form.calendar_id}
            onChange={handleChange('calendar_id')}
            fullWidth
            helperText="For syncing individual worker calendars"
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            disabled={saving || success}
            sx={{ fontWeight: 700, py: 1.5, borderRadius: 2 }}
          >
            {saving ? 'Adding...' : 'Add Team Member'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default TeamMemberForm;
